import { useEffect, useState } from "react";

import { grailCurrencies } from "../shared/currency";

import { Overline } from "@/components/typography";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "cn";

interface CurrencySelectProps {
  value: string | null;
  onChange: (value: string | null) => void;
  locked?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  className?: string;
  triggerClassName?: string;
  disabledReason?: string;
}

export default function CurrencySelect(props: CurrencySelectProps) {
  const { value, onChange, locked, disabled, hideLabel, className, triggerClassName, disabledReason } = props;
  const [localValue, setLocalValue] = useState(value);
  const isDisabled = locked || disabled;
  const selectedCurrency = grailCurrencies.find((option) => option.code === localValue);
  const tooltipTitle = locked
    ? `Currency is locked to ${localValue}`
    : disabled
      ? (disabledReason || "Remove selected talent to change your currency")
      : "";

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (next: string | null) => {
    setLocalValue(next);
    onChange(next);
  };

  const trigger = (
    <Select
      value={localValue}
      onValueChange={(next) => handleChange(next ? String(next) : null)}
      disabled={isDisabled}
    >
      <SelectTrigger
        id="currency-select"
        className={cn("my-1 h-10.5 w-35 min-w-30 bg-white", triggerClassName)}
      >
        <SelectValue>
          {selectedCurrency ? (
            <>
              <img
                width="20"
                src={selectedCurrency.flagUrl}
                srcSet={selectedCurrency.flag2x}
                alt=""
                className="mr-1.5 align-middle"
              />
              {selectedCurrency.label}
            </>
          ) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent alignItemWithTrigger={false}>
        {grailCurrencies.map((option) => (
          <SelectItem key={option.code} value={option.code}>
            <span className="mr-1 inline-flex items-center">
              <img
                loading="lazy"
                width="20"
                src={option.flagUrl}
                srcSet={option.flag2x}
                alt=""
                className="mr-1.5 align-middle"
              />
            </span>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className={cn(hideLabel ? "w-full" : "inline-block", className)}>
      {hideLabel ? null : <Overline>Currency</Overline>}
      {tooltipTitle ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger render={<div className="inline-block" />}>
              {trigger}
            </TooltipTrigger>
            <TooltipContent>{tooltipTitle}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        trigger
      )}
    </div>
  );
}
